/**
 * 时间格式化工具
 *
 * - formatDateTime: 通用 YYYY-MM-DD HH:mm:ss
 * - formatRelativeTime: 相对时间（按用户本地时区的日历日判断今天/昨天/本周；时刻均为本地显示）
 */

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六']

/** 允许的服务端/设备时钟超前量（毫秒） */
const RELATIVE_TIME_CLOCK_SKEW_MS = 5 * 60 * 1000

/** 本地日历为「明天」但时间戳距现在 <24h 时，按距今间隔显示，避免 UTC/东八区跨日误判为日期 */
const FUTURE_ONE_DAY_REINTERPRET_MAX_MS = 24 * 60 * 60 * 1000

/** 本地日历日当天 0 点的时间戳（用于与用户时区一致的「日」比较，避免纯毫秒/24h 与日历错位） */
function startOfLocalDayMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/**
 * 与 MyInvitationsPage 等页面一致：相对时间前先统一为带 Z 的 ISO（UTC）或毫秒，减少各端 Date 解析差异。
 */
export function toRelativeTimeInput(
  v: string | number | Date | null | undefined
): string | number | null | undefined {
  if (v == null || v === '') return v
  if (typeof v === 'string' || typeof v === 'number') return v
  if (v instanceof Date) return v.toISOString()
  return undefined
}

function parseInstant(input: string | number | Date | null | undefined): Date | null {
  if (input == null || input === '') return null
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input
  }
  if (typeof input === 'number' && Number.isFinite(input)) {
    const d = new Date(input)
    return Number.isNaN(d.getTime()) ? null : d
  }
  // 不对无时区 ISO 盲目补 Z：若服务端实为东八区墙钟，补 Z 会当成 UTC，与「现在」差约 7～8 小时
  const s = String(input).trim()
  if (!s) return null
  if (/^-?\d+$/.test(s)) {
    const n = Number(s)
    if (!Number.isFinite(n)) return null
    const ms = n >= 1e12 ? n : n >= 1e9 && n < 1e12 ? n * 1000 : n
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatShortLocalDate(date: Date, now: Date): string {
  const y = date.getFullYear()
  const yNow = now.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return y === yNow ? `${month}/${day}` : `${y}-${month}-${day}`
}

/**
 * 通用日期时间格式化：YYYY-MM-DD HH:mm:ss（本地时区）
 * @param date Date 或 ISO 字符串或 null/undefined
 * @returns 格式化字符串，无效或空则返回空字符串。使用 getFullYear/getMonth/... 故输出为本地时间。
 */
export function formatDateTime(
  date: Date | string | number | null | undefined
): string {
  if (date == null) return ''
  const d = typeof date === 'object' ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

/**
 * 相对时间（全部按运行环境本地时区解析与展示）
 * - 「今天 / 昨天」按本地日历日界线，不用「已过毫秒 ÷ 24h」近似（避免与用户对「昨天」的直觉不一致）
 * - 「昨天 HH:mm」「星期*」中的时刻为本地时分
 * @param dateInput ISO 字符串（含 Z 时为 UTC 瞬间）、毫秒时间戳等
 */
export function formatRelativeTime(dateInput: string | number | Date | null | undefined): string {
  const normalized = toRelativeTimeInput(dateInput)
  const date = parseInstant(
    normalized !== undefined ? normalized : (dateInput as string | number | Date | null | undefined)
  )
  if (!date) return ''

  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diff / (1000 * 60))
  const diffHours = Math.floor(diff / (1000 * 60 * 60))

  const todayStart = startOfLocalDayMs(now)
  const dateStart = startOfLocalDayMs(date)
  const calendarDayDiff = Math.round((todayStart - dateStart) / 86400000)

  if (diff < 0 && calendarDayDiff === -1 && diff > -FUTURE_ONE_DAY_REINTERPRET_MAX_MS) {
    const past = -diff
    const m = Math.floor(past / (1000 * 60))
    const h = Math.floor(past / (1000 * 60 * 60))
    if (past < 60 * 1000) return '刚刚'
    if (m < 60) return `${m}分钟前`
    if (h >= 1) return `${h}小时前`
    return `${m}分钟前`
  }

  if (calendarDayDiff < 0) {
    return formatShortLocalDate(date, now)
  }

  if (calendarDayDiff === 0) {
    if (diff < 0) {
      if (diff > -RELATIVE_TIME_CLOCK_SKEW_MS) return '刚刚'
      const hh = String(date.getHours()).padStart(2, '0')
      const mm = String(date.getMinutes()).padStart(2, '0')
      return `今天 ${hh}:${mm}`
    }
    if (diffMinutes < 1) return '刚刚'
    if (diffMinutes < 60) return `${diffMinutes}分钟前`
    if (diffHours >= 1) return `${diffHours}小时前`
    return `${diffMinutes}分钟前`
  }

  if (calendarDayDiff === 1) {
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `昨天 ${hours}:${minutes}`
  }

  if (calendarDayDiff >= 2 && calendarDayDiff < 7) {
    const weekday = date.getDay()
    return `星期${WEEKDAYS[weekday]}`
  }

  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${month}/${day}`
}
